import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import InlineSpinner from '../common/InlineSpinner';
import CompanyDetailModal from './CompanyDetailModal';
import UpdateDetailModal from './UpdateDetailModal';
import MetricsGrid from './MetricsGrid';
import styles from './VCUpdatesPage.module.css';

interface Update {
  id: string;
  company_name: string;
  company_icon?: string;
  update_title: string;
  update_date: string;
  metrics?: Array<{
    label: string;
    value: string;
    trend?: 'up' | 'down' | 'neutral';
    delta?: string;
    category: string;
    icon?: string;
  }>;
  highlights: string[];
  status: 'sent' | 'draft' | 'overdue';
}

const VCUpdatesPage: React.FC = () => {
  const [updates, setUpdates] = useState<Update[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [selectedUpdateId, setSelectedUpdateId] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    companyId: '',
    startDate: '',
    endDate: '',
  });
  const [companies, setCompanies] = useState<any[]>([]);

  useEffect(() => {
    loadUpdates();
  }, [filters]);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadUpdates = async () => {
    setLoading(true);
    try {
      const response = await apiService.getPortfolioUpdates(filters);
      if (response.success) {
        // Transform API data to match Update interface
        const transformedUpdates = response.data.map((update: any) => ({
          id: update.id,
          company_name: update.company_name,
          update_title: update.email_subject || 'Update',
          update_date: update.notified_at,
          metrics: update.metrics || undefined,
          highlights: update.highlights || extractHighlights(update.email_body),
          status: 'sent' as const,
        }));
        setUpdates(transformedUpdates);
      }
    } catch (err: any) {
      console.error('Failed to load updates:', err);
      // Fall back to empty array on error
      setUpdates([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await apiService.getVCPortfolio();
      if (response.success) {
        setCompanies(response.data || []);
      }
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  };

  const extractHighlights = (html: string): string[] => {
    if (!html) return [];
    
    // Simple extraction - look for bullet points or key phrases
    const highlights: string[] = [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const listItems = doc.querySelectorAll('li');
    
    listItems.forEach((li, idx) => {
      if (idx < 3) { // Take first 3
        const text = li.textContent?.trim();
        if (text) {
          highlights.push(text);
        }
      }
    });
    
    return highlights;
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <InlineSpinner />
        <span>Loading portfolio updates...</span>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Portfolio Updates</h1>
          <p className={styles.subtitle}>Historical updates from all portfolio companies</p>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <select 
          value={filters.companyId}
          onChange={(e) => setFilters({...filters, companyId: e.target.value})}
          className={styles.filterSelect}
        >
          <option value="">All Companies</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.company_name}
            </option>
          ))}
        </select>
        
        <input
          type="date"
          value={filters.startDate}
          onChange={(e) => setFilters({...filters, startDate: e.target.value})}
          className={styles.filterDate}
          placeholder="Start Date"
        />
        
        <input
          type="date"
          value={filters.endDate}
          onChange={(e) => setFilters({...filters, endDate: e.target.value})}
          className={styles.filterDate}
          placeholder="End Date"
        />
        
        {(filters.companyId || filters.startDate || filters.endDate) && (
          <button 
            onClick={() => setFilters({ companyId: '', startDate: '', endDate: '' })}
            className={styles.filterClear}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Stats */}
      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>TOTAL UPDATES</div>
          <div className={styles.statValue}>{updates.length}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>COMPANIES</div>
          <div className={`${styles.statValue} ${styles.statGreen}`}>{companies.length}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>THIS MONTH</div>
          <div className={`${styles.statValue} ${styles.statGreen}`}>
            {updates.filter(u => {
              const updateDate = new Date(u.update_date);
              const now = new Date();
              return updateDate.getMonth() === now.getMonth() && 
                     updateDate.getFullYear() === now.getFullYear();
            }).length}
          </div>
        </div>
      </div>

      {/* Updates List */}
      {updates.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📝</div>
          <h3 className={styles.emptyTitle}>No updates yet</h3>
          <p className={styles.emptyDescription}>
            Updates from your portfolio companies will appear here
          </p>
        </div>
      ) : (
        <div className={styles.updatesList}>
          {updates.map((update) => (
            <div 
              key={update.id} 
              className={styles.updateCard}
              onClick={() => setSelectedUpdateId(update.id)}
            >
              <div className={styles.updateHeader}>
                <div className={styles.updateCompany}>
                  <div className={styles.companyIcon}>
                    {update.company_icon || update.company_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className={styles.companyName}>{update.company_name}</div>
                    <div className={styles.updateTitle}>{update.update_title}</div>
                  </div>
                </div>
                <div className={styles.updateMeta}>
                  <div className={styles.updateDate}>
                    {new Date(update.update_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })}
                  </div>
                  <span className={styles.statusBadge}>{update.status.toUpperCase()}</span>
                </div>
              </div>

              {update.metrics && update.metrics.length > 0 && (
                <MetricsGrid metrics={update.metrics} />
              )}

              <div className={styles.updateHighlights}>
                <div className={styles.highlightsLabel}>KEY HIGHLIGHTS</div>
                <div className={styles.highlightsList}>
                  {update.highlights.map((highlight, idx) => (
                    <span key={idx} className={styles.highlightBadge}>
                      {highlight}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedCompanyId && (
        <CompanyDetailModal
          companyId={selectedCompanyId}
          onClose={() => setSelectedCompanyId(null)}
        />
      )}

      {selectedUpdateId && (
        <UpdateDetailModal
          updateId={selectedUpdateId}
          onClose={() => setSelectedUpdateId(null)}
        />
      )}
    </div>
  );
};

export default VCUpdatesPage;

